import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, MapPin, ArrowUp, ArrowDown, ArrowUpDown, Download, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { PROVIDER_TYPES } from '../../lib/pipStates';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

const TYPE_COLORS = { law_firm: '#F59E0B', marketer: '#8B5CF6', medical_practice: '#10B981', insurance_company: '#3B82F6', expert_service: '#223368' };
const TYPE_LABELS = { law_firm: 'Law Firm', marketer: 'Marketer', medical_practice: 'Medical Practice', insurance_company: 'Insurance', expert_service: 'Expert Service' };
const PROVIDER_TYPE_LABELS = Object.fromEntries(PROVIDER_TYPES.map(t => [t.value, t.label]));

// CSV export of everything on the platform. Three shapes: one row per
// organization (members + locations folded into cells), one per member, or
// one per location — the last two open cleanly as mailing/call lists.
const EXPORTS = [
  ['orgs', 'Organizations', 'One row per org: contact, subscription, owner, members, locations'],
  ['members', 'Members', 'One row per team member with email, phone and role'],
  ['locations', 'Locations', 'One row per office with full address and phone'],
];
function ExportMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const run = async (rows) => {
    setBusy(rows); setOpen(false);
    try { await api.adminExportOrganizationsCsv(rows); }
    catch (e) { alert(e.message); }
    finally { setBusy(''); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)} disabled={!!busy}
        className="flex items-center gap-2 px-4 py-2.5 bg-elevated border border-theme text-primary-c rounded-[10px] text-[13px] font-semibold hover:bg-hover-surface transition-all disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-[300px] bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50">
            {EXPORTS.map(([k, label, hint]) => (
              <button key={k} onClick={() => run(k)} className="w-full text-left px-4 py-2.5 hover:bg-hover-surface transition-all">
                <div className="text-[13px] font-semibold text-primary-c">{label}</div>
                <div className="text-[11px] text-muted-c">{hint}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Signed-up-since presets for the date filter; 'custom' reveals a from/to pair.
const DATE_PRESETS = [
  { value: '', label: 'Any Date' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'ytd', label: 'This year' },
  { value: 'custom', label: 'Custom range…' },
];

// mode: 'orgs' (law firms + practices; marketers excluded) or 'marketers'
// (the invite-only marketer accounts, on their own tab so they never
// inflate the organization numbers).
export default function OrganizationList({ mode = 'orgs' }) {
  const isMarketers = mode === 'marketers';
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Hide soft-deleted orgs (deleted_at IS NOT NULL) by default. Admins can
  // still toggle them back in to audit / restore. is_active is a separate
  // axis — orgs that the operator manually deactivated still show here so
  // they can be re-activated.
  const [showDeleted, setShowDeleted] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' }); // matches the server's default order
  const [orgs, setOrgs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());   // org ids with locations shown
  useEffect(() => {
    setLoading(true);
    api.adminListOrgs(showDeleted)
      .then((list) => setOrgs((list || []).filter((o) => isMarketers ? o.type === 'marketer' : o.type !== 'marketer')))
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, [showDeleted, isMarketers]);

  // Created-at window from the date preset (or the custom from/to pair).
  // Bounds are inclusive; `to` runs to end-of-day so picking today works.
  const createdWindow = (() => {
    if (datePreset === 'custom') {
      return {
        from: dateFrom ? new Date(dateFrom + 'T00:00:00') : null,
        to: dateTo ? new Date(dateTo + 'T23:59:59.999') : null,
      };
    }
    if (datePreset === 'ytd') return { from: new Date(new Date().getFullYear(), 0, 1), to: null };
    if (datePreset) return { from: new Date(Date.now() - Number(datePreset) * 86400000), to: null };
    return { from: null, to: null };
  })();

  const filtered = (orgs || []).filter(o => {
    if (typeFilter && o.type !== typeFilter) return false;
    if (practiceFilter && o.provider_type !== practiceFilter) return false;
    if (stateFilter && o.state?.toUpperCase() !== stateFilter) return false;
    if (statusFilter === 'active' && !o.is_active) return false;
    if (statusFilter === 'inactive' && o.is_active) return false;
    if (createdWindow.from || createdWindow.to) {
      const created = new Date(o.created_at);
      if (createdWindow.from && created < createdWindow.from) return false;
      if (createdWindow.to && created > createdWindow.to) return false;
    }
    if (!search) return true;
    const term = search.toLowerCase();
    return o.name?.toLowerCase().includes(term) || o.city?.toLowerCase().includes(term) || o.state?.toLowerCase().includes(term);
  });

  const states = [...new Set((orgs || []).map(o => o.state?.toUpperCase()).filter(Boolean))].sort();

  // Column sorting — click a header to sort by it, click again to flip.
  // First click uses the direction that reads naturally for the column
  // (dates newest-first, text A→Z, status active-first).
  const SORT_DEFAULT_DIR = { name: 'asc', type: 'asc', location: 'asc', created: 'desc', source: 'asc', status: 'desc' };
  const toggleSort = (key) => setSort(prev =>
    prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: SORT_DEFAULT_DIR[key] || 'asc' }
  );
  const sortValue = (o, key) => {
    switch (key) {
      case 'name': return (o.name || '').toLowerCase();
      case 'type': return `${TYPE_LABELS[o.type] || o.type || ''} ${PROVIDER_TYPE_LABELS[o.provider_type] || ''}`.toLowerCase();
      case 'location': return [o.city, o.state].filter(Boolean).join(', ').toLowerCase();
      case 'created': return new Date(o.created_at).getTime() || 0;
      case 'source': return (o.referral_source || '').toLowerCase();
      case 'status': return o.is_active ? 1 : 0;
      default: return 0;
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const SortHeader = ({ label, k, className = '' }) => (
    <th className={`text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 ${className}`}>
      <button
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold hover:text-primary-c transition-colors"
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        {sort.key === k
          ? (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
          : <ArrowUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  );

  const inputClass = 'px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[1200px] mx-auto px-4 md:px-7 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c">{isMarketers ? 'Marketers' : 'Organizations'}</h1>
            <p className="text-[13px] text-muted-c mt-0.5">{filtered.length} of {(orgs || []).length} {isMarketers ? 'marketers · invite-only, free, same tools as a law firm' : 'organizations'}</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu />
            <Link
              to={isMarketers ? '/admin/organizations/new?type=marketer' : '/admin/organizations/new'}
              className="flex items-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all"
            >
              {isMarketers ? '+ Invite Marketer' : '+ Invite Org'}
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 mb-4">
          <input
            type="text"
            className={inputClass + ' flex-1 min-w-[200px]'}
            placeholder="Search by name, city, state..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {!isMarketers && <select className={inputClass} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).filter(([k]) => k !== 'marketer').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>}
          <select className={inputClass} value={practiceFilter} onChange={e => setPracticeFilter(e.target.value)}>
            <option value="">All Practice Areas</option>
            {PROVIDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select className={inputClass} value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
            <option value="">All States</option>
            {states.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          <select className={inputClass} value={datePreset} onChange={e => setDatePreset(e.target.value)}>
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {datePreset === 'custom' && (
            <>
              <input
                type="date"
                className={inputClass}
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                aria-label="Created from"
              />
              <input
                type="date"
                className={inputClass}
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                aria-label="Created to"
              />
            </>
          )}
          <select className={inputClass} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <label className="flex items-center gap-2 px-3 py-[11px] text-[12px] text-muted-c cursor-pointer select-none hover:text-primary-c transition-colors">
            <input
              type="checkbox"
              className="accent-[#0DA7CA]"
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
            />
            Show deleted
          </label>
        </div>

        {loading ? (
          <LoadingSpinner size="lg" className="py-16" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={search || typeFilter || practiceFilter || stateFilter || statusFilter || datePreset ? 'No results' : 'No organizations'}
            description={search ? 'Try different filters.' : 'Invite your first organization to get started.'}
          />
        ) : (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-theme bg-tertiary">
                    <SortHeader label="Organization" k="name" />
                    <SortHeader label="Type" k="type" className="hidden md:table-cell" />
                    <SortHeader label="Location" k="location" className="hidden lg:table-cell" />
                    <SortHeader label="Created" k="created" className="hidden lg:table-cell" />
                    <SortHeader label="Source" k="source" className="hidden xl:table-cell" />
                    <SortHeader label="Status" k="status" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {sorted.map(org => {
                    const color = TYPE_COLORS[org.type] || '#6B7280';
                    const initials = org.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                    const location = [org.city, org.state].filter(Boolean).join(', ');
                    const locs = org.locations || [];
                    const isOpen = expanded.has(org.id);
                    return (
                      <React.Fragment key={org.id}>
                      <tr className="hover:bg-hover-surface transition-all">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            {locs.length > 0 ? (
                              <button
                                onClick={() => setExpanded(prev => {
                                  const next = new Set(prev);
                                  if (next.has(org.id)) next.delete(org.id); else next.add(org.id);
                                  return next;
                                })}
                                title={`${locs.length} location${locs.length === 1 ? '' : 's'}`}
                                className="w-5 h-5 flex items-center justify-center rounded text-muted-c hover:text-primary-c hover:bg-tertiary transition-all flex-shrink-0"
                              >
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                            ) : <span className="w-5 flex-shrink-0" />}
                            <Link to={`/admin/organizations/${org.id}`} className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: color }}>
                                {initials}
                              </div>
                              <span className="text-[13px] font-semibold text-primary-c hover:text-accent-light transition-colors">
                                {org.name}
                                {locs.length > 0 && (
                                  <span className="ml-2 text-[10px] font-semibold text-muted-c bg-tertiary px-1.5 py-0.5 rounded-full">
                                    {locs.length} location{locs.length === 1 ? '' : 's'}
                                  </span>
                                )}
                              </span>
                            </Link>
                          </div>
                        </td>
                        <td className="px-5 py-3 hidden md:table-cell">
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ color, background: `${color}15` }}>
                            {TYPE_LABELS[org.type] || org.type?.replace('_', ' ')}
                          </span>
                          {org.provider_type && (
                            <div className="text-[10px] text-muted-c mt-1">{PROVIDER_TYPE_LABELS[org.provider_type] || org.provider_type}</div>
                          )}
                        </td>
                        <td className="px-5 py-3 hidden lg:table-cell text-[12px] text-secondary-c">{location || '—'}</td>
                        <td className="px-5 py-3 hidden lg:table-cell text-[12px] text-muted-c">{new Date(org.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-3 hidden xl:table-cell">
                          {org.referral_source
                            ? <span className="text-[11px] font-mono text-accent-light">{org.referral_source}</span>
                            : <span className="text-[12px] text-muted-c">—</span>}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${org.is_active ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
                            {org.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                      {isOpen && locs.map(l => (
                        <tr key={l.id} className="bg-tertiary/40">
                          <td className="px-5 py-2">
                            <div className="flex items-center gap-2 pl-7">
                              <MapPin size={12} className="text-muted-c flex-shrink-0" />
                              <span className="text-[12px] text-secondary-c">{l.name}</span>
                              {l.is_primary && (
                                <span className="text-[9px] font-semibold text-accent-light bg-accent-glow px-1.5 py-0.5 rounded-full">Primary</span>
                              )}
                              {l.lat == null && (
                                <span className="text-[9px] font-semibold text-muted-c bg-tertiary px-1.5 py-0.5 rounded-full">No pin</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-2 hidden md:table-cell text-[11px] text-muted-c">Location</td>
                          <td className="px-5 py-2 hidden lg:table-cell text-[12px] text-secondary-c">
                            {[l.city, l.state].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td className="px-5 py-2 hidden lg:table-cell text-[11px] text-muted-c">
                            {[l.address_line1, l.zip].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td className="px-5 py-2 hidden xl:table-cell text-[11px] text-muted-c">{l.phone || '—'}</td>
                          <td className="px-5 py-2" />
                        </tr>
                      ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
